import React, { useContext, useState, useEffect } from 'react';
import {
    Box,
    Button,
    MenuItem,
    TextField,
    Autocomplete,
    Checkbox,
    ListItemText,
    Chip,
    Grid,
} from '@mui/material';
import { CommonContext, MasterDataContext } from '../../context';
import { delivery_type_id } from '../../utils/config';
import CommonDateRangePicker from '../common/CommonDateRangePicker';
import '../../assets/styles/FilterBar.scss';

export default function FilterBar() {
    const {
        courierList,
        merchant,
        orderEvents,
        dropUsers,
        csUsers,
        onAllUserDrop,
        courierData,
        commonMerchant,
        user
    } = useContext(CommonContext);
    const { filters, setFilters, initialFilters, onExport } = useContext(MasterDataContext);

    const [showAllMerchants, setShowAllMerchants] = useState(false);
    const [showAllCouriers, setShowAllCouriers] = useState(false);
    const [showAllBookingUsers, setShowAllBookingUsers] = useState(false);
    const [showAllCSUsers, setShowAllCSUsers] = useState(false);
    const [showAllStatuses, setShowAllStatuses] = useState(false);

    useEffect(() => {
        courierData();
        commonMerchant();
        onAllUserDrop();
    }, []);

    const handleChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const handleDateRangeChange = ({ startDate, endDate }) => {
        setFilters(prev => ({
            ...prev,
            dateRange: { startDate, endDate }
        }));
    };

    const handleReset = () => {
        setFilters(initialFilters);
    };

    const allUsers = dropUsers || [];

    return (
        <Box className="shipment-filter-bar">
            <Grid container spacing={2}>
                <Grid item size={{ xs: 12, md: 3 }}>
                    <div className="search-box">
                        <TextField
                            select
                            name="searchBy"
                            value={filters.searchBy}
                            onChange={(e) => handleChange('searchBy', e.target.value)}
                            size="small"
                            className="search-select"
                        >
                            <MenuItem value="lrnum">LR No.</MenuItem>
                            <MenuItem value="awb_no">AWB No.</MenuItem>
                            <MenuItem value="order_no">Order No.</MenuItem>
                            <MenuItem value="customer_mobile">Customer Mobile</MenuItem>
                            <MenuItem value="invoice_no">Invoice No</MenuItem>
                        </TextField>

                        <TextField
                            size="small"
                            placeholder="Enter value here"
                            value={filters.searchValue}
                            onChange={(e) => handleChange('searchValue', e.target.value)}
                            className="search-input"
                        />
                    </div>
                </Grid>

                {user?.user_type !== 'Merchant' && (
                    <Grid item size={{ xs: 12, md: 3 }}>
                        <Autocomplete
                            multiple
                            options={merchant || []}
                            getOptionLabel={(option) => option.company_name || ''}
                            value={(merchant || []).filter(item => filters.merchant_id.includes(item.id))}
                            onChange={(e, newValue) => handleChange('merchant_id', newValue.map(item => item.id))}
                            disableCloseOnSelect
                            limitTags={1}
                            renderOption={(props, option, { selected }) => (
                                <li {...props} key={option.id}>
                                    <Checkbox checked={selected} />
                                    <ListItemText primary={option.company_name} />
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Merchants"
                                    size="small"
                                    onClick={() => setShowAllMerchants(true)}
                                />
                            )}
                            sx={{
                                width: '100%',
                                '& .MuiInputBase-root': {
                                    height: showAllMerchants ? 'auto !important' : '30px',
                                    overflow: 'hidden'
                                }
                            }}
                        />
                    </Grid>
                )}

                <Grid item size={{ xs: 12, md: 3 }}>
                    <Autocomplete
                        multiple
                        options={courierList || []}
                        getOptionLabel={(option) => `${option.id} - ${option.full_name}`}
                        value={(courierList || []).filter(item => filters.courier_id.includes(item.id))}
                        onChange={(e, newValue) => handleChange('courier_id', newValue.map(item => item.id))}
                        disableCloseOnSelect
                        limitTags={1}
                        renderOption={(props, option, { selected }) => (
                            <li {...props} key={option.id}>
                                <Checkbox checked={selected} />
                                <ListItemText primary={`${option.id} - ${option.full_name}`} />
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Select Couriers"
                                size="small"
                                onClick={() => setShowAllCouriers(true)}
                            />
                        )}
                        sx={{
                            width: '100%',
                            '& .MuiInputBase-root': {
                                height: showAllCouriers ? 'auto !important' : '30px',
                                overflow: 'hidden'
                            }
                        }}
                    />
                </Grid>

                <Grid item size={{ xs: 12, md: 3 }}>
                    <TextField
                        select
                        label="Delivery Type"
                        value={filters.delivery_type_id}
                        onChange={(e) => handleChange('delivery_type_id', e.target.value)}
                        fullWidth
                        size="small"
                    >
                        <MenuItem value="">All Types</MenuItem>
                        {Object.entries(delivery_type_id).map(([key, value]) => (
                            <MenuItem value={key} key={key}>{value}</MenuItem>
                        ))}
                    </TextField>
                </Grid>

                <Grid item size={{ xs: 12, md: 3 }}>
                    <Autocomplete
                        multiple
                        options={allUsers}
                        getOptionLabel={(option) => option.name || ''}
                        value={allUsers.filter(item => filters.booking_user_id.includes(item.id))}
                        onChange={(e, newValue) => handleChange('booking_user_id', newValue.map(item => item.id))}
                        disableCloseOnSelect
                        limitTags={1}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Booking User"
                                size="small"
                                onClick={() => setShowAllBookingUsers(true)}
                            />
                        )}
                        sx={{
                            width: '100%',
                            '& .MuiInputBase-root': {
                                height: showAllBookingUsers ? 'auto !important' : '30px',
                                overflow: 'hidden'
                            }
                        }}
                    />
                </Grid>

                <Grid item size={{ xs: 12, md: 3 }}>
                    <Autocomplete
                        multiple
                        options={csUsers || []}
                        getOptionLabel={(option) => option.name || ''}
                        value={(csUsers || []).filter(item => filters.cs_user_id.includes(item.id))}
                        onChange={(e, newValue) => handleChange('cs_user_id', newValue.map(item => item.id))}
                        disableCloseOnSelect
                        limitTags={1}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="CS User"
                                size="small"
                                onClick={() => setShowAllCSUsers(true)}
                            />
                        )}
                        sx={{
                            width: '100%',
                            '& .MuiInputBase-root': {
                                height: showAllCSUsers ? 'auto !important' : '30px',
                                overflow: 'hidden'
                            }
                        }}
                    />
                </Grid>

                <Grid item size={{ xs: 12, md: 3 }}>
                    <Autocomplete
                        multiple
                        options={orderEvents || []}
                        getOptionLabel={(option) => option.event_name || ''}
                        value={(orderEvents || []).filter(item => filters.order_status_id.includes(Number(item.event_code)))}
                        onChange={(e, newValue) => handleChange('order_status_id', newValue.map(item => Number(item.event_code)))}
                        disableCloseOnSelect
                        limitTags={1}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Order Status"
                                size="small"
                                onClick={() => setShowAllStatuses(true)}
                            />
                        )}
                        sx={{
                            width: '100%',
                            '& .MuiInputBase-root': {
                                height: showAllStatuses ? 'auto !important' : '30px',
                                overflow: 'hidden'
                            }
                        }}
                    />
                </Grid>

                <Grid item size={{ xs: 12, md: 3 }}>
                    <Box className="date-picker-wrapper">
                        <CommonDateRangePicker
                            startDate={filters?.dateRange?.startDate}
                            endDate={filters?.dateRange?.endDate}
                            onChange={handleDateRangeChange}
                        />
                    </Box>
                </Grid>

                <Grid item xs={12} md={12}>
                    <Box className="filter-actions">
                        <Button className="search-btn" variant="contained" onClick={() => onExport()}>
                            Export
                        </Button>
                        <Button className="reset-btn" variant="outlined" onClick={handleReset}>
                            Reset
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}
